import type { EnhancedAsset } from '@bitte-ai/types';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { formatNumber } from '../../../lib/format';
import { cn } from '../../../lib/utils';
import { Card } from '../../ui/card';
import { AssetIcon } from '../account/AssetIcon';

interface TokenRowProps {
  rank: number;
  token: EnhancedAsset;
  visibleColumns: { id: string; label: string; show: boolean }[];
}

export function TokenRow({ rank, token, visibleColumns }: TokenRowProps) {
  const dayChange = token.priceChanges?.day ?? 0;
  const isPositiveChange = dayChange >= 0;

  const renderColumn = (columnId: string) => {
    switch (columnId) {
      case 'rank':
        return <div className="bitte-text-sm lg:bitte-text-base">{rank}</div>;
      case 'token':
        return (
          <div className="bitte-flex bitte-items-center bitte-gap-2 lg:bitte-gap-3">
            {token.meta?.tokenIcon ? (
              <img
                src={token.meta.tokenIcon}
                alt={token.symbol}
                className="bitte-w-6 bitte-h-6 lg:bitte-w-8 lg:bitte-h-8"
              />
            ) : (
              <AssetIcon
                symbol={token.symbol}
                className="bitte-w-6 bitte-h-6 lg:bitte-w-8 lg:bitte-h-8"
              />
            )}
            <div className="bitte-flex bitte-flex-col bitte-justify-center">
              <p className="bitte-font-medium bitte-text-sm lg:bitte-text-base">
                {token.symbol}
              </p>
              <div className="bitte-flex bitte-items-center bitte-gap-1">
                {token.icon && (
                  <img
                    src={token.icon}
                    alt="chain"
                    className="bitte-w-2 bitte-h-2 lg:bitte-w-3 lg:bitte-h-3"
                  />
                )}
                <p className="bitte-text-xs lg:bitte-text-sm bitte-text-muted-foreground">
                  {token.chains.join(', ')}
                </p>
              </div>
            </div>
          </div>
        );
      case 'price': {
        if (!token.priceData?.day || token.priceData.day.length === 0)
          return '-';
        const price = token.priceData.day[0][1];
        return (
          <div>
            <p className="bitte-font-medium bitte-text-sm lg:bitte-text-[14px] ">
              ${price}
            </p>
          </div>
        );
      }
      case 'balance': {
        if (!token.balances?.balance) return '-';
        return (
          <div>
            <p className="bitte-font-small bitte-text-sm lg:bitte-text-[14px] ">
              {formatNumber(token.balances.balance)}{' '}
              <span className="bitte-text-muted-foreground">
                {token.symbol}
              </span>
            </p>
          </div>
        );
      }
      case 'change':
        if (!token.priceChanges || dayChange === 0) return '-';
        return (
          <div>
            <p
              className={cn(
                'bitte-font-medium bitte-text-sm lg:bitte-text-[14px]',
                isPositiveChange
                  ? 'bitte-text-mb-green-text'
                  : 'bitte-text-mb-red-text',
              )}
            >
              {isPositiveChange ? '+' : ''}
              {dayChange.toFixed(2)}%
            </p>
          </div>
        );
      case 'chart': {
        if (!token.priceData?.month) return '-';

        const monthData = token.priceData.month;
        const chartData = monthData.map(([timestamp, price]) => ({
          timestamp,
          price,
          min: Math.min(...monthData.map(([, p]) => p)) * 0.95,
        }));

        return (
          <div className="bitte-w-full bitte-h-6 lg:bitte-h-8">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              >
                <defs>
                  <linearGradient
                    id={`gradient-${token.symbol}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor={
                        isPositiveChange
                          ? 'rgb(74, 222, 128)'
                          : 'rgb(248, 113, 113)'
                      }
                      stopOpacity={0.2}
                    />
                    <stop
                      offset="95%"
                      stopColor={
                        isPositiveChange
                          ? 'rgb(74, 222, 128)'
                          : 'rgb(248, 113, 113)'
                      }
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="price"
                  stroke={
                    isPositiveChange
                      ? 'rgb(74, 222, 128)'
                      : 'rgb(248, 113, 113)'
                  }
                  fill={`url(#gradient-${token.symbol})`}
                  strokeWidth={1.5}
                  fillOpacity={1}
                  isAnimationActive={false}
                  dot={false}
                  baseValue={0}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        );
      }
      case 'value':
        if (!token.usdValue) return '-';
        return (
          <div>
            <p className="bitte-font-medium bitte-text-sm lg:bitte-text-[14px]">
              ${formatNumber(token.usdValue, 'value')}
            </p>
          </div>
        );
      case 'volume24h':
        if (!token.volume24h) return '-';
        return (
          <div>
            <p className="bitte-font-medium bitte-text-sm lg:bitte-text-[14px]">
              ${formatNumber(token.volume24h, 'value')}
            </p>
          </div>
        );
      case 'marketcap':
        if (!token.marketcap) return '-';
        return (
          <div>
            <p className="bitte-font-medium bitte-text-sm lg:bitte-text-base">
              ${formatNumber(token.marketcap)}
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Card className="bitte-p-2 lg:bitte-p-4 hover:bitte-bg-accent/50 !bitte-bg-mb-black-50 bitte-transition-colors bitte-min-w-[1000px] bitte-max-w-[1000px] bitte-mx-auto">
      <div
        className="bitte-grid bitte-items-center bitte-w-full"
        style={{
          gridTemplateColumns: `30px ${visibleColumns
            .slice(1)
            .map((col) => {
              if (col.id === 'token') return '1.5fr';
              if (col.id === 'balance') return '1.5fr';
              return '1fr';
            })
            .join(' ')}`,
        }}
      >
        {visibleColumns.map((column) => (
          <div key={column.id}>{renderColumn(column.id)}</div>
        ))}
      </div>
    </Card>
  );
}
